import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AgGridReact } from 'ag-grid-react';
import {
  Box,
  Button,
  Container,
  Paper,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  InputAdornment,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Stack
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Visibility,
  Refresh,
  Search,
  Clear,
  CheckCircle,
  Cancel as CancelIcon,
  Person,
  MoneyOff,
  Print as PrintIcon
} from '@mui/icons-material';
import api from '../../services/api';

// Constants
const DATE_OPTIONS = { year: 'numeric', month: 'short', day: 'numeric' };
const CURRENCY_OPTIONS = { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 };
const NUMBER_OPTIONS = {};

// Utility functions (memoized)
const formatCurrency = (value) => {
  if (value == null) return '$0.00';
  return new Intl.NumberFormat('en-US', CURRENCY_OPTIONS).format(value);
};

const formatNumber = (value) => {
  if (value == null) return '0';
  return new Intl.NumberFormat('en-US', NUMBER_OPTIONS).format(value);
};

const formatDate = (value) => {
  if (!value) return '';
  return new Date(value).toLocaleDateString('en-US', DATE_OPTIONS);
};

// Custom Cell Renderers (memoized components)
const StatusChip = React.memo(({ value }) => (
  <Chip
    label={value ? 'PAID' : 'UNPAID'}
    color={value ? 'success' : 'warning'}
    size="small"
    icon={value ? <CheckCircle /> : <CancelIcon />}
    sx={{ fontFamily: 'Inter', fontWeight: 600 }}
  />
));

const AmountCell = React.memo(({ value, color = 'primary' }) => (
  <Typography
    variant="body2"
    color={color}
    fontWeight="bold"
    sx={{
      fontFamily: 'Inter',
      fontWeight: 600,
      fontSize: '0.875rem',
      textAlign: 'center',
      display: 'block'
    }}
  >
    {formatCurrency(value)}
  </Typography>
));

const ChipCell = React.memo(({ value, label, color = 'primary', icon = null }) => (
  <Chip
    label={value}
    size="small"
    color={color}
    icon={icon}
    sx={{ fontFamily: 'Inter', fontWeight: 500 }}
  />
));

const ActionButtons = React.memo(({ data, onView, onEdit, onDelete }) => (
  <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
    <Tooltip title="View/Print">
      <IconButton size="small" color="primary" onClick={() => onView(data)}>
        <Visibility fontSize="small" />
      </IconButton>
    </Tooltip>
    <Tooltip title="Edit">
      <IconButton
        size="small"
        color="info"
        onClick={() => onEdit(data)}
        disabled={data.isPaid}
      >
        <Edit fontSize="small" />
      </IconButton>
    </Tooltip>
    <Tooltip title="Delete">
      <IconButton
        size="small"
        color="error"
        onClick={() => onDelete(data)}
        disabled={data.isPaid}
      >
        <Delete fontSize="small" />
      </IconButton>
    </Tooltip>
  </Box>
));

const CommissionRequestList = () => {
  const navigate = useNavigate();
  const gridRef = useRef();
  
  // State
  const [loading, setLoading] = useState(true);
  const [rowData, setRowData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    agent: ''
  });
  const [agents, setAgents] = useState([]);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, request: null });
  const [deleting, setDeleting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Memoized column definitions
  const columnDefs = useMemo(() => [
    {
      field: 'receiptNo',
      headerName: 'Receipt No',
      width: 160,
      cellRenderer: (params) => (
        <Chip
          label={params.value || 'N/A'}
          size="small"
          color="info"
          variant="outlined"
          sx={{ fontFamily: 'Inter', fontWeight: 500, fontSize: '0.75rem' }}
        />
      )
    },
    {
      field: 'transDate',
      headerName: 'Date',
      width: 110,
      valueFormatter: (params) => formatDate(params.value),
      cellStyle: { fontFamily: 'Inter', fontWeight: 400 }
    },
    {
      field: 'agentName',
      headerName: 'Agent',
      width: 180,
      cellRenderer: (params) => (
        <Typography variant="body2" sx={{ fontFamily: 'Inter', fontWeight: 400, textAlign: 'center' }}>
          {params.value || 'N/A'}
        </Typography>
      )
    },
    {
      field: 'creditAmount',
      headerName: 'Credit Amount',
      width: 150,
      cellRenderer: (params) => <AmountCell value={params.value} color="primary" />
    },
    {
      field: 'totalDeductions',
      headerName: 'Total Deductions',
      width: 150,
      cellRenderer: (params) => <AmountCell value={params.value} color="error" />
    },
    {
      field: 'netAmount',
      headerName: 'Net Amount',
      width: 150,
      cellRenderer: (params) => <AmountCell value={params.value} color="success.main" />
    },
    {
      field: 'applicantCount',
      headerName: 'Applicants',
      width: 120,
      cellRenderer: (params) => (
        <ChipCell value={formatNumber(params.value)} label={formatNumber(params.value)} color="primary" icon={<Person />} />
      )
    },
    {
      field: 'deductionCount',
      headerName: 'Deductions',
      width: 120,
      cellRenderer: (params) => (
        <ChipCell
          value={formatNumber(params.value)}
          label={formatNumber(params.value)}
          color="secondary"
          variant="outlined"
          icon={params.value > 0 ? <MoneyOff /> : null}
        />
      )
    },
    {
      field: 'isPaid',
      headerName: 'Status',
      width: 130,
      cellRenderer: (params) => <StatusChip value={params.value} />
    },
    {
      field: 'createdDate',
      headerName: 'Created',
      width: 120,
      valueFormatter: (params) => formatDate(params.value),
      cellStyle: { fontFamily: 'Inter', fontWeight: 400 }
    },
    {
      headerName: 'Actions',
      width: 130,
      pinned: 'right',
      cellRenderer: (params) => (
        <ActionButtons
          data={params.data}
          onView={handleView}
          onEdit={handleEdit}
          onDelete={handleDeleteClick}
        />
      )
    }
  ], []);

  const defaultColDef = useMemo(() => ({
    sortable: true,
    filter: true,
    resizable: true,
    flex: 1,
    minWidth: 100
  }), []);

  // Data processing helper
  const processCommissionData = useCallback((items) => {
    return items.map((item) => {
      const deductions = item.deductions || item.Deductions || [];
      const applicants = item.applicants || item.Applicants || [];
      const totalDeductions = deductions.reduce((sum, d) => sum + (d.debitAmount || d.DebitAmount || 0), 0);
      const creditAmount = item.creditAmount || item.CreditAmount || 0;

      return {
        cid: item.cid || item.CID,
        receiptNo: item.receiptNo || item.ReceiptNo || 'N/A',
        transDate: item.transDate || item.TransDate,
        creditAmount,
        isPaid: item.isPaid || item.IsPaid || false,
        createdDate: item.createdDate || item.CreatedDate,
        remark: item.remark || item.Remark,
        deductions,
        applicants,
        totalDeductions,
        netAmount: creditAmount - totalDeductions,
        applicantCount: applicants.length,
        deductionCount: deductions.length,
        agentName: item.agentName || item.AgentName || 'N/A',
        agentId: item.agentID || item.AgentID || item.agentId,
        _raw: item
      };
    });
  }, []);

  // API calls
  const fetchAgents = useCallback(async () => {
    try {
      const response = await api.get('/companies/agents');
      if (response.data && Array.isArray(response.data)) {
        setAgents(response.data);
      }
    } catch (error) {
      console.error('Error fetching agents:', error);
    }
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/commissionrequest');
      const processedData = processCommissionData(response.data);
      setRowData(processedData);
      setFilteredData(processedData);
      await fetchAgents();
    } catch (error) {
      console.error('Error fetching commission requests:', error);
      const errorMessage = error.response?.data?.message || 
                          error.response?.data || 
                          error.message || 
                          'Failed to load commission requests.';
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [fetchAgents, processCommissionData]);

  // Filter data
  const applyFilters = useCallback((data, { search, status, agent }) => {
    let filtered = [...data];

    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(item =>
        (item.receiptNo?.toLowerCase().includes(searchLower)) ||
        (item.agentName?.toLowerCase().includes(searchLower)) ||
        (item.remark?.toLowerCase().includes(searchLower))
      );
    }

    if (status !== 'all') {
      filtered = filtered.filter(item => status === 'paid' ? item.isPaid : !item.isPaid);
    }

    if (agent) {
      filtered = filtered.filter(item => item.agentId === agent);
    }

    return filtered;
  }, []);

  // Effects
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const filtered = applyFilters(rowData, filters);
    setFilteredData(filtered);
  }, [filters, rowData, applyFilters]);

  // Handlers
  const handleView = useCallback((data) => {
    navigate(`/commissions/print/${data.cid}`);
  }, [navigate]);

  const handleEdit = useCallback((data) => {
    navigate(`/commissions/edit/${data.cid}`);
  }, [navigate]);

  const handleDeleteClick = useCallback((data) => {
    setDeleteDialog({ open: true, request: data });
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    const { request } = deleteDialog;
    if (!request) return;

    try {
      setDeleting(true);
      await api.delete(`/commissionrequest/${request.cid}`);
      
      setSnackbar({
        open: true,
        message: 'Commission request deleted successfully!',
        severity: 'success'
      });
      
      setDeleteDialog({ open: false, request: null });
      fetchData();
    } catch (error) {
      console.error('Error deleting commission request:', error);
      const errorMessage = error.response?.data?.message || 
                          error.response?.data || 
                          error.message || 
                          'Failed to delete commission request.';
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setDeleting(false);
    }
  }, [deleteDialog, fetchData]);

  const handleRefresh = useCallback(() => {
    fetchData();
  }, [fetchData]);

  const handleClearFilters = useCallback(() => {
    setFilters({ search: '', status: 'all', agent: '' });
  }, []);

  const handleExport = useCallback(() => {
    gridRef.current?.api.exportDataAsCsv();
  }, []);

  const handleFilterChange = useCallback((key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  }, []);

  // Computed values
  const stats = useMemo(() => ({
    total: filteredData.length,
    totalCredit: filteredData.reduce((sum, item) => sum + (item.creditAmount || 0), 0),
    totalDeductions: filteredData.reduce((sum, item) => sum + (item.totalDeductions || 0), 0),
    totalNet: filteredData.reduce((sum, item) => sum + (item.netAmount || 0), 0),
    paidCount: filteredData.filter(item => item.isPaid).length,
    unpaidCount: filteredData.filter(item => !item.isPaid).length,
    totalApplicants: filteredData.reduce((sum, item) => sum + (item.applicantCount || 0), 0),
    totalDeductionEntries: filteredData.reduce((sum, item) => sum + (item.deductionCount || 0), 0)
  }), [filteredData]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100vh', p: 0, bgcolor: '#f5f5f5' }}>
      <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems="center" mb={2} spacing={1}>
          <Box>
            <Typography variant="h5" component="h1" sx={{ fontFamily: 'Inter', fontWeight: 700 }}>
              Commission Requests
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ fontFamily: 'Inter' }}>
              Manage all commission requests
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<Add />}
              onClick={() => navigate('/commissions/create')}
              sx={{ fontFamily: 'Inter', fontWeight: 600 }}
            >
              New Request
            </Button>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={handleRefresh}
              sx={{ fontFamily: 'Inter', fontWeight: 600 }}
            >
              Refresh
            </Button>
          </Stack>
        </Stack>

        {/* Filters */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={2} alignItems="center">
          <TextField
            size="small"
            placeholder="Search..."
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            sx={{ flexGrow: 1, minWidth: 200 }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><Search /></InputAdornment>,
              endAdornment: filters.search && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => handleFilterChange('search', '')}>
                    <Clear />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              label="Status"
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="paid">Paid</MenuItem>
              <MenuItem value="unpaid">Unpaid</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Agent</InputLabel>
            <Select
              value={filters.agent}
              onChange={(e) => handleFilterChange('agent', e.target.value)}
              label="Agent"
            >
              <MenuItem value="">All Agents</MenuItem>
              {agents.map((agent) => (
                <MenuItem key={agent.companyID} value={agent.companyID}>
                  {agent.companyName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant="outlined"
            size="small"
            startIcon={<Clear />}
            onClick={handleClearFilters}
            sx={{ fontFamily: 'Inter', fontWeight: 500 }}
          >
            Clear
          </Button>

          <Box sx={{ flexGrow: 1 }} />

          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="textSecondary" sx={{ fontFamily: 'Inter', fontWeight: 500 }}>
              Total: {stats.total} requests
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={handleExport}
              sx={{ fontFamily: 'Inter', fontWeight: 500 }}
            >
              Export CSV
            </Button>
          </Stack>
        </Stack>

        {/* Grid */}
        <Box sx={{ flex: 1, minHeight: 400, width: '100%' }}>
          <div className="ag-theme-alpine" style={{ height: '100%', width: '100%' }}>
            <AgGridReact
              ref={gridRef}
              rowData={filteredData}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              enableCellTextSelection
              ensureDomOrder
              pagination
              paginationPageSize={50}
              paginationPageSizeSelector={[20, 50, 100]}
              rowHeight={32}
              onGridReady={(params) => {
                setTimeout(() => params.api.sizeColumnsToFit(), 100);
              }}
            />
          </div>
        </Box>

        {/* Stats */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} mt={2} pt={2} borderTop={1} borderColor="divider">
          <StatBox label="Total Requests" value={stats.total} />
          <StatBox label="Total Credit" value={formatCurrency(stats.totalCredit)} color="primary" />
          <StatBox label="Total Deductions" value={formatCurrency(stats.totalDeductions)} color="error" />
          <StatBox label="Total Net" value={formatCurrency(stats.totalNet)} color="success.main" />
          <StatBox label="Paid / Unpaid" value={`${stats.paidCount} / ${stats.unpaidCount}`} />
          <StatBox label="Total Applicants" value={formatNumber(stats.totalApplicants)} />
          <StatBox label="Deduction Entries" value={formatNumber(stats.totalDeductionEntries)} />
        </Stack>
      </Paper>

      {/* Dialogs */}
      <DeleteDialog
        open={deleteDialog.open}
        request={deleteDialog.request}
        deleting={deleting}
        onClose={() => setDeleteDialog({ open: false, request: null })}
        onConfirm={handleDeleteConfirm}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// Helper Components
const StatBox = React.memo(({ label, value, color = 'text.primary' }) => (
  <Box>
    <Typography variant="caption" color="textSecondary" sx={{ fontFamily: 'Inter', fontWeight: 500, display: 'block' }}>
      {label}
    </Typography>
    <Typography variant="h6" color={color} sx={{ fontFamily: 'Inter', fontWeight: 700 }}>
      {value}
    </Typography>
  </Box>
));

const DeleteDialog = React.memo(({ open, request, deleting, onClose, onConfirm }) => (
  <Dialog open={open} onClose={onClose}>
    <DialogTitle sx={{ fontFamily: 'Inter', fontWeight: 700 }}>
      Confirm Delete
    </DialogTitle>
    <DialogContent>
      <DialogContentText sx={{ fontFamily: 'Inter' }}>
        Are you sure you want to delete this commission request?
        <br /><br />
        <strong>Receipt No:</strong> {request?.receiptNo || 'N/A'}<br />
        <strong>Agent:</strong> {request?.agentName || 'N/A'}<br />
        <strong>Amount:</strong> {formatCurrency(request?.creditAmount)}<br />
        <strong>Status:</strong> {request?.isPaid ? 'PAID' : 'UNPAID'}<br />
        <strong>Applicants:</strong> {formatNumber(request?.applicantCount)}<br />
        <strong>Deductions:</strong> {formatNumber(request?.deductionCount)}
        <br /><br />
        {!request?.isPaid && (
          <Alert severity="warning">
            This action cannot be undone. Make sure this request is not needed.
          </Alert>
        )}
      </DialogContentText>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose} disabled={deleting}>
        Cancel
      </Button>
      <Button
        onClick={onConfirm}
        variant="contained"
        color="error"
        disabled={deleting}
        startIcon={deleting ? <CircularProgress size={20} /> : <Delete />}
      >
        {deleting ? 'Deleting...' : 'Delete'}
      </Button>
    </DialogActions>
  </Dialog>
));

export default CommissionRequestList;